/**
 * PaymentRequiredBanner Styles
 * SESSION_332: Stripe Auto-Charge on Approval - Limited Access UI
 *
 * Distinctive amber/gold styling for payment required banners.
 * Design direction: Warm, action-oriented, sophisticated gold tones.
 * Follows the same pattern as ClaimStatusBanner for consistency.
 */

import { css, keyframes } from '@emotion/react';

/**
 * Subtle glow animation for the payment icon
 */
export const glowKeyframes = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 0 30px rgba(251, 191, 36, 0.6);
  }
`;

/**
 * Shimmer animation for the accent line
 */
export const shimmerKeyframes = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
`;

/**
 * Slide in animation for banner entrance
 */
export const slideInKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Base banner container styles
 */
export const bannerContainerStyles = css`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  animation: ${slideInKeyframes} 0.4s ease-out;
`;

/**
 * Payment required banner styles - warm amber/gold gradient
 */
export const paymentBannerStyles = css`
  background: linear-gradient(135deg, #D97706 0%, #F59E0B 40%, #FBBF24 100%);
  box-shadow:
    0 4px 20px rgba(217, 119, 6, 0.3),
    0 2px 8px rgba(217, 119, 6, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
`;

/**
 * Inner content wrapper with subtle overlay
 */
export const contentWrapperStyles = css`
  position: relative;
  z-index: 1;
  padding: 20px 24px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
    z-index: -1;
  }
`;

/**
 * Geometric accent shape in background
 */
export const geometricAccentStyles = css`
  position: absolute;
  top: -20px;
  right: -20px;
  width: 140px;
  height: 140px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  z-index: 0;
`;

/**
 * Secondary geometric accent
 */
export const geometricAccentSecondaryStyles = css`
  position: absolute;
  bottom: -40px;
  right: 60px;
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 50%;
  z-index: 0;
`;

/**
 * Third geometric accent - small diamond shape
 */
export const geometricAccentTertiaryStyles = css`
  position: absolute;
  top: 50%;
  right: 180px;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-50%) rotate(45deg);
  z-index: 0;
`;

/**
 * Icon container with glow effect
 */
export const iconContainerStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  flex-shrink: 0;
  animation: ${glowKeyframes} 2.5s ease-in-out infinite;
`;

/**
 * Title text styles
 */
export const titleStyles = css`
  font-family: 'Comfortaa', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
`;

/**
 * Message text styles
 */
export const messageStyles = css`
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  line-height: 1.5;
`;

/**
 * Business name highlight styles
 */
export const businessNameStyles = css`
  font-weight: 600;
  color: #ffffff;
`;

/**
 * Accent line with shimmer effect
 */
export const accentLineContainerStyles = css`
  position: relative;
  height: 3px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 16px;
`;

/**
 * Animated shimmer for accent line
 */
export const accentLineShimmerStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 50%,
    transparent 100%
  );
  animation: ${shimmerKeyframes} 2.5s ease-in-out infinite;
`;

/**
 * CTA button styles - prominent action button
 */
export const ctaButtonStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  color: #B45309;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover:not(:disabled) {
    background: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

/**
 * Feature list container
 */
export const featureListStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
`;

/**
 * Individual feature badge
 */
export const featureBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.9);
`;
